import { sendBanEmail, unbanUser, usePaymentDefaultUsers } from '@gozoki/api'
import { includesLowerCase } from '@gozoki/tools'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useToast } from '~/components/communication/Toast'
import { UsersHeaderActions } from '~/components/routes/users/users-header-actions'
import { USERS_TAB_PERMISSIONS } from '~/components/routes/users/tabs'
import { paymentDefaultUsersColumns } from '~/components/routes/users/users-columns'
import { UsersPageLayout, UsersSearchGrid } from '~/components/routes/users/users-page-layout'
import { useDeleteUser } from '~/components/routes/users/use-delete-selection'
import { withRoutePermissions } from '~/utils/rbac'
import { getErrorMessageWithSentry } from '~/utils/sentry'

export const Route = createFileRoute('/_authenticated/users/payment-defaults')({
  staticData: {
    permission: USERS_TAB_PERMISSIONS['payment-defaults'],
  },
  component: PaymentDefaultUsersPage,
})
withRoutePermissions(Route)

function PaymentDefaultUsersPage() {
  const [Toast, showToast] = useToast()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<number[]>([])

  const { data: paymentDefaultUsers, refetch, isLoading } = usePaymentDefaultUsers()

  const users = useMemo(() => paymentDefaultUsers ?? [], [paymentDefaultUsers])

  const rows = useMemo(
    () =>
      users.filter((user) =>
        includesLowerCase(`${user.firstName} ${user.lastName} ${user.email}`, search),
      ),
    [users, search],
  )

  const handleDelete = useDeleteUser(selected, showToast, () => {
    setSelected([])
    return refetch()
  })

  const sendEmailMut = useMutation({
    mutationFn: (id: number) => sendBanEmail(id),
    onError: (error) => {
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
    onSuccess: () => {
      showToast({ message: 'Email envoyé', severity: 'success' })
    },
  })

  const unbanMut = useMutation({
    mutationFn: (id: number) => unbanUser(id),
    onError: (error) => {
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
    onSuccess: () => {
      showToast({ message: 'Utilisateur débanni', severity: 'success' })
      refetch()
    },
  })

  return (
    <UsersPageLayout
      activeTab="payment-defaults"
      actions={
        <UsersHeaderActions
          activeTab="payment-defaults"
          selected={selected}
          handleDelete={handleDelete}
        />
      }
    >
      <Toast />
      <UsersSearchGrid
        search={search}
        onSearchChange={setSearch}
        columns={paymentDefaultUsersColumns(sendEmailMut.mutateAsync, unbanMut.mutateAsync)}
        rows={rows}
        loading={isLoading}
        onRowSelectionModelChange={setSelected}
      />
    </UsersPageLayout>
  )
}
